import { cn } from '@/lib/utils'
import type { Dayjs } from 'dayjs'
import { ArrowDownWideNarrowIcon, PlusIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import Dialog from '../guides/Dialog'
import ButtonBase from './Button/ButtonBase'
import {
    type EventoCalendario,
    diasDaGrade,
    eventosDoDia,
    isHoje,
    pedacoDoDia,
    periodoArrastado,
} from './calendario-mensal.utils'
import { Label } from './ui/label'

export type { EventoCalendario }

type CalendarioMensalProps = {
    /** `YYYY-MM` exibido. */
    competencia: string
    eventos: EventoCalendario[]
    /** `dia` é o dia em que o evento foi clicado, dentro do período dele. */
    onEventoClick?: (id: string, dia: Dayjs) => void
    /**
     * Com ela, a célula de um dia do mês cria o evento: clique simples passa
     * só o início (`fim` nulo); clicar e arrastar passa o período percorrido.
     */
    onDiaAdd?: (inicio: Dayjs, fim: Dayjs | null) => void
    /**
     * Clicar e arrastar seleciona um período para o `onDiaAdd`. Desligado,
     * só o clique único — para eventos de um dia, como feriado.
     */
    permiteArraste?: boolean
    /** Rótulo do botão de eventos ocultos; padrão `+N mais`. */
    rotuloOcultos?: (quantidade: number) => string
}

const corFimDeSemana = (dia: Dayjs) =>
    dia.day() === 0
        ? 'text-error'
        : dia.day() === 6
          ? // `color:` explícito: sem ele o tailwind-merge lê o valor
            // arbitrário como tamanho de fonte e descarta o `text-*` vizinho
            'text-[color:color-mix(in_oklab,_var(--error)_70%,_white)]'
          : 'text-inherit'

/** Calendário mensal com eventos multi-dia como barra contínua e popup "+N". */
export default function CalendarioMensal({
    competencia,
    eventos,
    onEventoClick,
    onDiaAdd,
    rotuloOcultos = quantidade => `+${quantidade} mais`,
    permiteArraste = false,
}: CalendarioMensalProps) {
    const [diaAberto, setDiaAberto] = useState<Dayjs | null>(null)
    const [arraste, setArraste] = useState<{
        apertou: Dayjs
        atual: Dayjs
    } | null>(null)
    const dias = diasDaGrade(competencia)

    // soltar pode acontecer fora da grade (ou sobre uma barra): escuta a janela
    useEffect(() => {
        if (!arraste) return
        function soltar() {
            if (!arraste) return
            const { inicio, fim } = periodoArrastado(
                arraste.apertou,
                arraste.atual,
            )
            setArraste(null)
            onDiaAdd?.(inicio, fim)
        }
        function cancelar() {
            setArraste(null)
        }
        window.addEventListener('pointerup', soltar)
        window.addEventListener('pointercancel', cancelar)
        return () => {
            window.removeEventListener('pointerup', soltar)
            window.removeEventListener('pointercancel', cancelar)
        }
    }, [arraste, onDiaAdd])

    const periodo = arraste
        ? periodoArrastado(arraste.apertou, arraste.atual)
        : null
    const isNoArraste = (dia: Dayjs) =>
        !!periodo &&
        !dia.isBefore(periodo.inicio, 'day') &&
        !dia.isAfter(periodo.fim ?? periodo.inicio, 'day')

    return [
        // igual ao corpo do DataTable: ocupa o espaço do pai e, se ele
        // limitar abaixo do piso das semanas, rola aqui dentro
        <div key="grade" className="flex flex-col flex-1 min-h-0 overflow-auto">
            <div
                className={cn(
                    // 6 semanas dividem a altura disponível; semana cujo conteúdo
                    // não cabe cresce até ele e o container rola
                    'grid grid-cols-7 grid-rows-[auto_repeat(6,minmax(min-content,1fr))]',
                    'flex-1 bg-card',
                    arraste && 'select-none',
                    // linhas internas só: a borda externa é do container — sem
                    // borda à direita no sábado nem embaixo na última semana
                    '[&>*:not(:nth-child(7n))]:border-r',
                    '[&>*:not(:nth-last-child(-n+7))]:border-b',
                )}
            >
                {dias.slice(0, 7).map(dia => (
                    <span
                        key={dia.day()}
                        className={cn(
                            'text-center text-sm font-semibold py-1 select-none',
                            'sticky top-0 z-10 bg-card',
                            corFimDeSemana(dia),
                        )}
                    >
                        {
                            dia
                                .format('dddd')
                                .replace(/^[a-z]/, w => w.toUpperCase())
                                .split('-')[0]
                        }
                    </span>
                ))}
                {dias.map(dia => {
                    const doMes = dia.format('YYYY-MM') === competencia
                    const pedaco = pedacoDoDia(eventos, dia, competencia)
                    // título longo atravessa os dias seguintes da mesma barra
                    const isTransborda =
                        !!pedaco?.mostraTitulo && pedaco.extensao > 1
                    return (
                        <div
                            key={dia.toISOString()}
                            // a célula toda conta, inclusive sobre as barras
                            onPointerEnter={() =>
                                arraste &&
                                doMes &&
                                setArraste({ ...arraste, atual: dia })
                            }
                            className={cn(
                                // container de largura: o número do dia escala com
                                // ela. Não é `size`: esse zera a altura de conteúdo
                                // da célula e a semana não cresceria para rolar
                                'relative min-w-0 flex flex-col gap-1 py-1',
                                '[container-type:inline-size]',
                                // acima das células seguintes, que vêm depois no DOM
                                isTransborda && 'z-[1]',
                                isHoje(dia) &&
                                    'bg-accent ring ring-ring rounded-sm',
                                !doMes && 'opacity-disabled',
                                isNoArraste(dia) && 'bg-primary/20',
                            )}
                        >
                            {onDiaAdd && doMes && (
                                // a célula inteira cria o evento; fica por
                                // baixo do conteúdo para as barras e o "+N"
                                // continuarem clicáveis (botão não aninha botão)
                                <ButtonBase
                                    variant="ghost"
                                    title={`Adicionar em ${dia.format('DD/MM/YYYY')}`}
                                    className="group/adicionar absolute inset-0 size-full rounded-none p-0 hover:bg-accent dark:hover:bg-accent"
                                    onPointerDown={evento => {
                                        if (
                                            !permiteArraste ||
                                            evento.button !== 0
                                        )
                                            return
                                        // toque captura o ponteiro no botão e
                                        // as outras células não recebem o enter
                                        evento.currentTarget.releasePointerCapture(
                                            evento.pointerId,
                                        )
                                        setArraste({ apertou: dia, atual: dia })
                                    }}
                                    // com arraste, o ponteiro é tratado no
                                    // soltar e aqui só entra o teclado
                                    // (Enter/Espaço chega com detail 0)
                                    onClick={evento =>
                                        (!permiteArraste ||
                                            evento.detail === 0) &&
                                        onDiaAdd(dia, null)
                                    }
                                >
                                    {/* sem hover (touch) fica sempre visível,
                                        senão a ação não se anuncia */}
                                    <PlusIcon
                                        className={cn(
                                            'absolute top-[3px] left-2 size-[clamp(1.5rem,18cqw,8rem)] text-primary',
                                            'opacity-0 transition-opacity',
                                            'group-hover/adicionar:opacity-100 group-focus-visible/adicionar:opacity-100',
                                            '[@media(hover:none)]:opacity-100',
                                        )}
                                    />
                                </ButtonBase>
                            )}
                            <span
                                className={cn(
                                    // não captura o clique: ele cai no botão da célula
                                    'relative pointer-events-none flex flex-1 items-start justify-end px-1',
                                )}
                            >
                                <span
                                    className={cn(
                                        'text-[clamp(1.5rem,18cqw,8rem)] leading-none font-semibold',
                                        'pr-2 select-none',
                                        corFimDeSemana(dia),
                                    )}
                                >
                                    {dia.format('D')}
                                </span>
                            </span>
                            <div className="relative flex flex-col gap-1">
                                {/* "+N" acima da barra: ela fica na mesma altura das vizinhas e o título que transborda não quebra */}
                                {pedaco && pedaco.ocultos > 0 && (
                                    <ButtonBase
                                        variant="ghost"
                                        onClick={() => setDiaAberto(dia)}
                                        title={`Exibir mais ${pedaco.ocultos} evento(s)`}
                                        className="mx-1 h-6 px-2 bg-muted/40 text-muted-foreground text-xs hover:bg-muted/75"
                                    >
                                        {rotuloOcultos(pedaco.ocultos)}
                                    </ButtonBase>
                                )}
                                {pedaco && (
                                    <ButtonBase
                                        variant="ghost"
                                        aria-hidden={!pedaco.abre || undefined}
                                        tabIndex={pedaco.abre ? undefined : -1}
                                        title={
                                            pedaco.evento.dica ??
                                            pedaco.evento.titulo
                                        }
                                        onClick={() =>
                                            onEventoClick?.(
                                                pedaco.evento.id,
                                                dia,
                                            )
                                        }
                                        style={
                                            pedaco.evento.cor
                                                ? {
                                                      backgroundColor:
                                                          pedaco.evento.cor,
                                                      color: pedaco.evento
                                                          .corTexto,
                                                  }
                                                : undefined
                                        }
                                        className={cn(
                                            'relative h-6 min-w-0 px-2 rounded-none font-normal',
                                            'select-none transition-opacity duration-200',
                                            isTransborda && 'overflow-visible',
                                            !pedaco.evento.cor
                                                ? 'bg-primary text-primary-foreground hover:bg-accent'
                                                : 'hover:opacity-80',
                                            pedaco.abre
                                                ? 'rounded-l-md ml-1'
                                                : '-ml-px',
                                            pedaco.fecha
                                                ? 'rounded-r-md mr-1'
                                                : '-mr-px',
                                            // sem clique, só o hover
                                            !onEventoClick && 'cursor-default',
                                            pedaco.evento.className,
                                        )}
                                    >
                                        {isTransborda ? (
                                            // largura da barra inteira (N dias)
                                            // menos o padding; corta só no fim dela.
                                            // Sem eventos de mouse: hover e clique
                                            // caem no pedaço do dia embaixo

                                            <span
                                                className="pointer-events-none absolute inset-y-0 left-2 truncate leading-6"
                                                style={{
                                                    width: `calc(${pedaco.extensao} * 100% - 1rem)`,
                                                }}
                                            >
                                                {pedaco.evento.titulo}
                                            </span>
                                        ) : (
                                            <span className="truncate">
                                                {pedaco.mostraTitulo
                                                    ? pedaco.evento.titulo
                                                    : ' '}
                                            </span>
                                        )}
                                    </ButtonBase>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>,
        <Dialog.Root
            key="popup"
            open={!!diaAberto}
            onClose={() => setDiaAberto(null)}
        >
            <Dialog.Title action="Eventos do dia">
                {diaAberto?.format('LL')}
            </Dialog.Title>
            <Dialog.Content>
                <Label className="mb-2 text-muted-foreground">
                    Maior prioridade{' '}
                    <ArrowDownWideNarrowIcon className="size-4" />
                </Label>
                {diaAberto &&
                    eventosDoDia(eventos, diaAberto).map(evento => (
                        <ButtonBase
                            variant="ghost"
                            key={evento.id}
                            title={evento.dica ?? evento.titulo}
                            onClick={() => {
                                setDiaAberto(null)
                                onEventoClick?.(evento.id, diaAberto)
                            }}
                            style={
                                evento.cor
                                    ? {
                                          backgroundColor: evento.cor,
                                          color: evento.corTexto,
                                      }
                                    : undefined
                            }
                            className={cn(
                                'mb-2 h-auto w-full p-2 rounded font-normal',
                                'hover:opacity-80',
                                !evento.cor &&
                                    'bg-primary text-primary-foreground hover:bg-primary/90',
                                !onEventoClick && 'cursor-default',
                                evento.className,
                            )}
                        >
                            <span className="truncate">{evento.titulo}</span>
                        </ButtonBase>
                    ))}
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton>
                    Fechar
                </Dialog.Actions.SecondaryButton>
            </Dialog.Actions>
        </Dialog.Root>,
    ]
}
